/**
 * 批量事务抽屉 V4.0 - 事务明细表格组件
 * 根据事务类型显示不同的明细字段，支持客户端状态追踪
 */

import React, { useState, useCallback, useMemo } from "react";
import { Table, Tag, message } from "antd";
import { useBatchTransactionContext } from "../context/BatchTransactionContext";
import { CLIENT_STATUS } from "../types";
import { getColumnsByTransactionType } from "../config/transactionDetailColumns";

/**
 * 事务明细表格组件
 */
const TransactionDetailTable = ({ transactionId, transactionType, isPreview = false }) => {
    const { state, transactionManager } = useBatchTransactionContext();

    // 从全局状态中获取当前事务的明细数据
    const transaction = state.transactions.find((t) => t.id === transactionId);
    const detailData = transaction?.details || [];

    // 过滤掉已删除的明细（除非是显示变更历史）
    const visibleDetails = useMemo(() => {
        return detailData.filter((detail) => detail.clientStatus !== CLIENT_STATUS.DELETED);
    }, [detailData]);

    // 处理明细字段更新
    const handleFieldUpdate = useCallback(
        (detailId, field, value) => {
            transactionManager.updateDetail(transactionId, detailId, { [field]: value });
        },
        [transactionId, transactionManager],
    );

    // 处理数量变更
    const handleQuantityChange = useCallback(
        (detailId, quantity) => {
            if (quantity > 0) {
                handleFieldUpdate(detailId, "quantity", quantity);
            }
        },
        [handleFieldUpdate],
    );

    // 处理备注变更
    const handleRemarksChange = useCallback(
        (detailId, remarks) => {
            handleFieldUpdate(detailId, "remarks", remarks);
        },
        [handleFieldUpdate],
    );

    // 处理特定字段变更（用于新事务类型的特殊字段）
    const handleFieldChange = useCallback(
        (detailId, fieldName, value) => {
            handleFieldUpdate(detailId, fieldName, value);
        },
        [handleFieldUpdate],
    );

    // 删除明细项
    const handleDeleteDetail = useCallback(
        (detailRecord) => {
            if (isPreview) {
                console.log("预览模式下不允许删除明细项");
                return;
            }

            // 直接删除，不需要确认
            transactionManager.deleteDetail(transactionId, detailRecord.id);
            message.success("明细已删除");
        },
        [isPreview, transactionId, transactionManager],
    );

    // 渲染客户端状态标签
    const renderClientStatusTag = (clientStatus) => {
        const statusConfig = {
            [CLIENT_STATUS.PRISTINE]: { color: "default", text: "原" },
            [CLIENT_STATUS.NEW]: { color: "green", text: "新" },
            [CLIENT_STATUS.MODIFIED]: { color: "orange", text: "改" },
            [CLIENT_STATUS.DELETED]: { color: "red", text: "删" },
        };

        const config = statusConfig[clientStatus] || { color: "default", text: "未知" };
        // 修复：只有 PRISTINE 状态时不显示标签，其他状态都显示
        return clientStatus === CLIENT_STATUS.PRISTINE ? null : (
            <Tag color={config.color} size="small">
                {config.text}
            </Tag>
        );
    };

    // 根据事务类型获取对应的列配置
    const typeSpecificColumns = getColumnsByTransactionType(transactionType, transactionId, isPreview, {
        handleQuantityChange,
        handleRemarksChange,
        handleDeleteDetail,
        handleFieldChange,
    });

    // 在第一列前添加客户端状态列，最后添加备注和操作列
    const columns = [
        {
            title: "状态",
            key: "client_status",
            width: 40,
            render: (_, record) => renderClientStatusTag(record.clientStatus),
        },
        ...typeSpecificColumns,
    ];

    if (visibleDetails.length === 0) {
        return <div style={{ textAlign: "center", padding: "20px 0", color: "#999" }}>暂无明细数据</div>;
    }

    return (
        <>
            <Table dataSource={visibleDetails} columns={columns} rowKey="id" pagination={false} size="small" style={{ marginTop: 8, marginBottom: 12 }} showHeader={false} />
        </>
    );
};

export default TransactionDetailTable;
